import React, { useCallback, useMemo, forwardRef } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet, { BottomSheetView, BottomSheetBackdrop, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import BottomSheetHeader from './BottomSheetHeader';

interface ActionBottomSheetProps {
  isVisible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const ActionBottomSheet = forwardRef<BottomSheet, ActionBottomSheetProps>(
  ({ isVisible, onClose, title, children }, ref) => {
    // Get screen dimensions and safe area insets
    const screenHeight = Dimensions.get('window').height;
    const insets = useSafeAreaInsets();

    // Fixed height calculation for 310px sheet (accommodates two action cards)
    const calculateContentHeight = () => {
      const fixedHeight = 310;

      return {
        totalHeight: fixedHeight,
        maxHeight: fixedHeight,
        needsScrolling: false
      };
    };

    // Calculate snap points based on dynamic content height
    const modalSnapPoints = useMemo(() => {
      const { totalHeight } = calculateContentHeight();
      const percentage = (totalHeight / screenHeight) * 100;
      return [`${percentage}%`];
    }, [screenHeight, title]);

    // Callbacks
    const handleSheetChanges = useCallback((index: number) => {
      if (index === -1) {
        onClose();
      }
    }, [onClose]);

    const renderBackdrop = useCallback(
      (props: any) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          opacity={0.4}
          enableTouchThrough={false}
          pressBehavior="close"
          style={[props.style, { height: screenHeight }]}
        />
      ),
      [screenHeight]
    );

    // Don't render the BottomSheet at all when not visible
    if (!isVisible) {
      return null;
    }

    return (
      <BottomSheet
        ref={ref}
        index={0}
        snapPoints={modalSnapPoints}
        onChange={handleSheetChanges}
        enablePanDownToClose
        enableDynamicSizing={false}
        backgroundStyle={styles.bottomSheetBackground}
        handleIndicatorStyle={styles.handleIndicator}
        backdropComponent={renderBackdrop}
        animateOnMount={true}
        android_keyboardInputMode="adjustResize"
        topInset={0}
        bottomInset={0}
        enableContentPanningGesture={false}
        enableHandlePanningGesture={true}
        activeOffsetY={[-1, 1]}
        failOffsetX={[-5, 5]}
      >
        <BottomSheetView style={styles.contentContainer}>
          {/* Header */}
          <View style={styles.headerSection}>
            <BottomSheetHeader 
              title={title} 
              onClose={onClose} 
            />
          </View>

          {/* Content */}
          {(() => {
            const { needsScrolling } = calculateContentHeight();

            if (needsScrolling) {
              return (
                <BottomSheetScrollView 
                  style={styles.scrollableContentSection}
                  contentContainerStyle={[styles.scrollableContent, { paddingBottom: Math.max(insets.bottom, 24) }]}
                  showsVerticalScrollIndicator={true}
                  indicatorStyle="default"
                >
                  <View style={styles.actionContainer}>
                    {children}
                  </View>
                </BottomSheetScrollView>
              );
            } else {
              return (
                <View style={[styles.contentSection, { paddingBottom: Math.max(insets.bottom, 24) }]}>
                  <View style={styles.actionContainer}>
                    {children}
                  </View>
                </View>
              );
            }
          })()}
        </BottomSheetView>
      </BottomSheet>
    );
  }
);

ActionBottomSheet.displayName = 'ActionBottomSheet';

export default ActionBottomSheet;

const styles = StyleSheet.create({
  bottomSheetBackground: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 24,
  },
  handleIndicator: {
    backgroundColor: '#DAD8CF',
    width: 40,
    height: 4,
  },
  contentContainer: {
    flex: 1,
  },
  headerSection: {
    flexShrink: 0, // Never shrink
  },
  contentSection: {
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 16,
    flex: 1, // Take available space but don't overflow
  },
  scrollableContentSection: {
    flex: 1, // Take all available space between header and footer
    paddingHorizontal: 24,
  },
  scrollableContent: {
    paddingTop: 8,
    flexGrow: 1, // Enable proper scrolling
  },
  actionContainer: {
    gap: 8,
  },
  actionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
    marginBottom: 8,
  },
});
